/**
 * Der Wertebereich der y-Achse (HM-054).
 *
 * Übernommen aus der Geometrie von HM-053, weil die beiden Regeln darin an
 * einem echten Diagramm entstanden sind:
 *
 * **Etwas Luft nach oben und unten**, damit die Kurve nicht am Rahmen klebt.
 * Bei einer **konstanten** Reihe gibt es keine Spanne, aus der sich Luft
 * ableiten ließe — dann eine feste, sonst läge die Linie auf der Kante und die
 * Skalenrechnung teilte durch null.
 *
 * **Die Achse geht nicht unter null, solange kein Wert es tut.** Eine Leistung
 * von 70 bis 2800 W bekam sonst eine Kante bei „−0,3 kW" — eine negative
 * Leistung heißt an einem Zähler Einspeisung, und die hat dort niemand
 * gemessen. Kommen tatsächlich negative Werte vor, bleibt die Luft darunter.
 */

export interface RangeOptions {
  /**
   * `zero` verankert die Achse an der Null — für Balken und gestapelte Flächen
   * zwingend: Ein Balken misst seine Länge ab der Grundlinie, und liegt die bei
   * 70 statt bei 0, ist ein Wert von 140 doppelt so lang wie einer von 105.
   */
  readonly baseline?: "auto" | "zero";
}

/**
 * Der Bereich zu einer Menge von Werten, oder `null`, wenn keiner vorliegt.
 *
 * `null` heißt „nichts zu zeigen" — die aufrufende Schicht zeichnet dann Achsen
 * ohne Kurve und schreibt hin, warum. Ein erfundener Bereich von 0 bis 1 sähe
 * aus wie eine Messreihe am Boden.
 */
export function valueRange(
  values: readonly (number | null)[],
  options: RangeOptions = {},
): readonly [number, number] | null {
  const known = values.filter((value): value is number => value !== null && Number.isFinite(value));
  if (known.length === 0) return null;
  return padRange(Math.min(...known), Math.max(...known), options);
}

/**
 * Derselbe Bereich aus einem bereits bekannten Kleinst- und Größtwert.
 *
 * Diese Fassung ist die, die im Betrieb läuft: Die Zeichenschicht kennt beide
 * Werte bereits und ruft die Rechnung bei jeder Datenänderung erneut auf. Ohne
 * sie müsste das Diagramm bei jedem Nachladen von vorn aufgebaut werden — und
 * ein Ablesefenster, das dabei zuklappt, macht die Seite unbenutzbar.
 */
export function padRange(
  low: number | null,
  high: number | null,
  options: RangeOptions = {},
): [number, number] {
  /**
   * Ohne Werte ein schmaler Bereich statt `null`: Die Achse steht dann leer da
   * und das Diagramm schreibt hin, warum. Ein Bereich von 0 bis 1 wäre eine
   * Behauptung, aber eine sichtbar leere Achse ist keine.
   */
  if (low === null || high === null || !Number.isFinite(low) || !Number.isFinite(high)) {
    return [0, 1];
  }

  const pad = (high - low) * 0.15 || Math.max(Math.abs(high) * 0.05, 1);
  if (options.baseline === "zero") return [Math.min(0, low), high + pad];
  return [low >= 0 ? Math.max(0, low - pad) : low - pad, high + pad];
}

/**
 * Eine Achse ab null, die **an einer beschrifteten Linie endet** — für Balken.
 *
 * Mit 15 % Luft nach oben endete die Skala zwischen zwei Marken: bei 19,7, die
 * oberste Beschriftung bei 17,5, und darüber lag ein leerer Streifen, der auf
 * einem Telefon die Einheit von ihren Zahlen trennte. Hier ist die Obergrenze
 * ein Vielfaches des Schritts, und der Schritt ist 1, 2 oder 5 × 10ⁿ — nie
 * 2,5: „17,5" ist eine Stelle breiter als „20", und die Achse links ist auf
 * einem Telefon jeden Bildpunkt wert.
 *
 * `divisions` ist die Höchstzahl der Abschnitte. `headroom` ist die Luft über
 * dem größten Wert: bei Balken ein Zwanzigstel, damit keiner an die Kante
 * stößt; bei Linien keine — sonst stünde ein Autarkiegrad von 100 % unter einer
 * Achse bis 120 %, einem Wert, den es nicht gibt.
 *
 * @returns `null`, wenn kein positiver Wert da ist — dann gilt `padRange`.
 */
export function zeroBasedTicks(
  high: number | null,
  options: { readonly divisions?: number; readonly headroom?: number } = {},
): { max: number; step: number } | null {
  if (high === null || !Number.isFinite(high) || high <= 0) return null;
  const divisions = options.divisions ?? 6;
  const target = high * (1 + (options.headroom ?? 0.05));
  const raw = target / divisions;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  /* Auf zwölf Stellen gerundet: 6 × 0,2 ist in Gleitkomma 1,2000000000000002. */
  const clean = (value: number): number => Number(value.toPrecision(12));
  for (const factor of [1, 2, 5, 10]) {
    const step = clean(factor * magnitude);
    const count = Math.ceil(clean(target / step));
    if (count <= divisions) return { max: clean(count * step), step };
  }
  /* Unerreichbar: Bei Faktor 10 ist der Schritt größer als `raw`. */
  return { max: Math.ceil(target / (10 * magnitude)) * 10 * magnitude, step: 10 * magnitude };
}
